import { useEffect, useState, createContext, useContext } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import axios from '../../../axios';

import { Link, useNavigate } from 'react-router-dom';

// table hook
import useTableLoad from '../../../hooks/useTableLoad';

// table context
import { TableIdProvider } from '../../../contexts/TableId';

// useMutation hook
import { useMutation, useQueryClient } from '@tanstack/react-query';

// dialog for deleting
import Dialog from '../Dialog';

// snackbar
import useSnackbarStore from '../../../store/snackbar';

import { muiTheme } from '../../theme';

import { Skeleton, IconButton, Select, MenuItem, Typography, Tooltip, CircularProgress } from '@mui/material';

import { formatDisplay, displayTruncated } from '../../util';

import { IconBox } from '../../Common';

import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import NorthRoundedIcon from '@mui/icons-material/NorthRounded';
import SouthRoundedIcon from '@mui/icons-material/SouthRounded';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import FloatingOptionsComponent from '../FloatingOptions';

import useIsMobile from '../../../hooks/useIsMobile';

import TableHeader from './TableHeader';
import PaginationBar from './PaginationBar';
import TableTabs from './TableTabs';
import FilterRibbon from './FilterRibbon';

import handleErrors from '../../errorHandler';

import TotalBar from './TotalBar';

const justify = $align => $align === "right" ? "flex-end" : $align === "center" ? "center" : "flex-start";

const Table = styled.div`
  /* border: 1px solid ${props => props.theme.colors.darkBg}; */
`;

const TableTitle = styled.div`
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  border-radius: 5px;
  color: ${props => props.theme.colors.primary};
  background-color: ${props => props.theme.colors.primaryFaint};
  width: fit-content;
  padding: .5rem 2rem;
`

const MainTable = styled.div`
  overflow-x: hidden;
`

const ScrollableTable = styled.div`
  overflow-x: auto;
`

/* ---- CSS-grid "table" ---- */

const GridTable = styled.div`
  /* owns the column tracks; rows subscribe to them via subgrid so content-based
     widths (max-content) are measured across the whole column and stay aligned */
  display: grid;
  grid-template-columns: var(--cols);
  width: 100%;
`;

const Rowgroup = styled.div`
  display: contents;
`;

const Bodygroup = styled.div`
  display: contents;

  @media ${props => props.theme.breaks.down("md")} {
    font-size: 1.2rem;
  }
`;

const rowBase = css/* closed */`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
`;

const HeaderRow = styled.div`
  ${rowBase};
  background-color: ${props => props.theme.colors.darkBg};
  font-weight: 700;

  @media ${props => props.theme.breaks.down("md")} {
    font-size: 1.2rem;
  }
`;

const BodyRow = styled.div`
  ${rowBase};
  background-color: #FFF;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  border-left: 1px solid ${props => props.theme.colors.border};
  border-right: 1px solid ${props => props.theme.colors.border};
  cursor: ${props => props.$view ? "pointer" : "default"};

  /* the row link has no box of its own (display: contents), so surface ITS
     keyboard focus on the row. Scoped with :has so focusing the expand or
     action buttons doesn't outline the whole row, and :focus-visible so a
     mouse click doesn't either. */
  &:has(> a:focus-visible) {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: -2px;
  }
`;

/* A single real anchor per row. display: contents removes the anchor's own box,
   so the data cells stay as normal grid items but are now *inside* the link:
   left-click navigates, right-click / Cmd-click / middle-click open in a new tab
   natively, and the cell text is selectable link text. */
const RowLink = styled(Link)`
  display: contents;
  color: inherit;
  text-decoration: none;

  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: inherit;
    text-decoration: none;
  }
`;

const cellBase = props => css/* closed */`
  display: flex;
  align-items: center;
  justify-content: ${justify(props.$align)};
  gap: ${props.$gap ? props.$gap : "0"};
  min-width: 0;                  /* lets long values truncate instead of blowing out the track */
  padding: .5rem 1rem;
`;

const HeaderCell = styled.div`
  ${cellBase};
  padding: 1rem 1rem;
  cursor: pointer;

  svg {
    position: relative;
    top: .2rem;
  }

  &:hover .sort-arrow {
    visibility: visible;
  }
`;

const HeaderSelectCell = styled(HeaderCell)`
  justify-content: center;
  padding: 0 0 0 1.5rem;
  cursor: default;
`;

const HeaderActionsCell = styled(HeaderCell)`
  padding-right: 1.5rem;
  cursor: default;
`;

const BodyCell = styled.div`
  ${cellBase};
  padding: ${props => props.$margin === "dense" ? ".3rem 1rem" : "1.2rem 1rem"};
  user-select: text;
  color: inherit;
  text-decoration: none;
  ${props => props.$last ? "padding-right: 1.5rem;" : ""}
  cursor: ${props => props.onClick ? "pointer" : "default"};

  @media ${props => props.theme.breaks.down('lg')} {
    padding: ${props => props.$margin === "dense" ? ".3rem 1rem" : ".6rem 1rem"};
    ${props => props.$last ? "padding-right: 1.5rem;" : ""}
  }
`;

const BodySelectCell = styled(BodyCell)`
  position: relative;
  justify-content: center;
  padding: 0 1rem 0 1.5rem;

  /* vertical divider, inset from the row's top/bottom borders */
  &::after {
    content: "";
    position: absolute;
    top: .8rem;
    bottom: .8rem;
    right: 0;
    width: 2px;
    border-radius: 1px;
    background-color: ${props => props.theme.colors.border};
  }
`;

const BodyActionsCell = styled(BodyCell)`
  padding-right: 1.5rem;
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  box-shadow: ${props => props.theme.shadows[1]};
  border-radius: 5px;
  border-top-right-radius: 2px;
  border-top-left-radius: 2px;
  margin: .2rem 0;
  background-color: ${props => props.theme.colors.darkBg};
  align-items: center;

  @media ${props => props.theme.breaks.down('md')} {
    flex-direction: column;
    gap: 1.5rem;
    padding: .5rem 1rem;
  }
`;

const PageOptions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media ${props => props.theme.breaks.down('md')} {
    width: 100%;
  }
`

const TotalShowing = styled.div`
  padding: .5rem;
  .stat {
    font-weight: 700;
  }
`;

const PageInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;
`;

const PageRowsMenu = styled(Select)`
  background-color: #FFF;
  padding: 0;
`;

const SortArrow = styled.div`
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
`

const DeleteMessage = styled.div`
  margin: 0 1.6rem;
`

const SkeletonComponent = ({ headers, numOfRows = 10, disableActions = false }) => {
  return (
    Array.from({ length: numOfRows }, (_, index) => (
      <BodyRow key={index} role="row">
        <BodySelectCell role="cell">
          <Skeleton variant="rounded" width={30} height={30} />
        </BodySelectCell>
        {headers.map((header) => (
          <BodyCell key={header.id} role="cell">
            <Skeleton variant="text" width="100%" />
          </BodyCell>
        ))}
        {!disableActions ? (
          <BodyActionsCell role="cell">
            <Skeleton variant="text" width="100%" />
          </BodyActionsCell>
        ) : null}
      </BodyRow>
    ))
  )
}

const viewResource = (navigate, resource, id, view) => {
  if (view) {
    let url = resource ? `/${resource}/view/${id}` : `view/${id}`
    navigate(url);
  }
}

const DeleteDialog = ({ url, row, idKey, group = false, warning_msg }) => {

  warning_msg = warning_msg ? warning_msg : <DeleteMessage>This action cannot be undone. Are you sure you want to delete this?</DeleteMessage>;
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const queryClient = useQueryClient();

  const { isMobile } = useIsMobile();

  const deleteResource = async (remove_group) => {
    let remove_all = remove_group ? `?remove_group=${remove_group}` : '';
    try {
      const { data } = await axios.delete(`/${url}/${row[idKey]}${remove_all}`);
      return data;
    } catch (e) {
      throw e;
    }
  }

  const { mutateAsync, isPending } = useMutation({
    mutationFn: deleteResource,
    onSuccess: () => {
      showSnackbar('Deleted successfully!', 'success');
      queryClient.invalidateQueries({ queryKey: [url] });
    },
    onError: (e) => {
      let message = handleErrors(e) || 'Failed to delete!';
      showSnackbar(message, 'error');
    }
  })

  const additionalButtons = group ?[{ text: "Delete Group", variant: "outlined", color: "secondary", func: () => mutateAsync(true)}] : [];

  return (
    <Dialog
      saveText="Confirm Delete"
      toggle={<Tooltip title="Delete">
      <IconBox color={muiTheme.palette.secondary.main} button size={isMobile ? "mini" : "small"} bg={muiTheme.palette.secondary.light}>
        <DeleteOutlinedIcon color="secondary" />
      </IconBox>
    </Tooltip>}
    text={warning_msg}
    title="Confirm Delete?"
    loading={isPending}
    saveFunc={() => mutateAsync()}
    additionalButtons={additionalButtons}
    >
    </Dialog>
  )
}



const TableComponent = ({tableId, title, icon, defaultSort = "-name", headers, mobile_headers, tabs = [], tabField = 'tab', defaultTab = "all", headersByTab = {}, operation = "server", filters, view = true, viewUrl = null, viewId = null, edit = true, trash = true, idKey="id", fieldsToSearch, additionalCollections, groupOption = false, NoTitle = false, additionalActions = [], createNew = true, disableSearch = false, del_warning_msg, disableActions = false, exportCsv = false, clickAction = null, ...props}) => {

  const { isMobile } = useIsMobile();
  const navigate = useNavigate();

  let use_headers = isMobile ? (mobile_headers || headers) : headers

  const { pagination: { page, rowsPerPage }, setPagination, data = {}, isLoading, sort, handleSort, handleTabs, query, sortedColumns, handleSearch } = useTableLoad({ tableId, headers: use_headers, defaultSort, rowsPerPage: 20, tabs, defaultTab, additionalCollections, tabField });

  const TitleIcon = icon;

  const getSorting = (sort) => {
    let sortOpts = sort.split("-");
    let sortDir = sortOpts.length > 1 ?'desc' : 'asc';
    let sortField = sortOpts[0] || sortOpts[1]
    return {field: sortField, dir: sortDir}
  }

  const sorting = getSorting(sort)

  const [viewCardStatus, setViewCardStatus] = useState({});

  const [margin, setMargin] = useState("normal") // normal, dense

  const toggleOpenViewCard = (id) => {
    setViewCardStatus({...viewCardStatus, [id]: !viewCardStatus[id]});
  }

  const toggleDensity = () => {
    setMargin(prevState => prevState === "normal" ? "dense" : "normal");
  }

  const handlePageRows = (e) => {
    setPagination(x => ({...x, rowsPerPage: e.target.value}));
  }

  const paginateRows = (rows) => {
    if (!rows) return [];
    let newRows = rows;
    if (operation === "server") {
      newRows = rows.slice(0, rowsPerPage);
    } else {
      newRows = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }
    return newRows;
  }

  const getViewableRows = (rows) => {
    let visibleRows = `0`;
    if (rows) {
      let begin = rowsPerPage * page + 1
      let visible = operation === "server" ? rows.length : rows.length - page * rowsPerPage;
      let end = begin + Math.min(rowsPerPage, visible) - 1;
      visibleRows = `${begin}-${end}`;
    }
    return visibleRows;
  }

  const totalPages = Math.floor(data?.count / rowsPerPage) || 0;

  const onPageChange = (newPage) => {
    setPagination(x => ({...x, page: newPage}));
  }

  // shared grid column template for header + every body row
  const colTemplate = `5rem repeat(${sortedColumns.length}, minmax(max-content, 1fr))${disableActions ? '' : ' 12rem'}`;
  const colCount = 1 + sortedColumns.length + (disableActions ? 0 : 1);

  const resourcePrefix = viewUrl ? `/${viewUrl}/` : "";
  const getRowUrl = (row) => `${resourcePrefix}view/${row[viewId || idKey]}`;

  const ariaSort = (header) => sorting.field === header.id
    ? (sorting.dir === "desc" ? "descending" : "ascending")
    : "none";

  // don't navigate when the click was really the user finishing a text selection
  const handleRowLinkClick = (e) => {
    const selection = typeof window !== "undefined" && window.getSelection && window.getSelection().toString();
    if (selection) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const actions = [];

  if (additionalActions.length) {
    actions.push(...additionalActions);
  }

  if (edit) {
    actions.push({
      tooltip: "Edit",
      Icon: EditOutlinedIcon,
      onClick: (id) => {
        navigate(`edit/${id}`);
      }
    })
  }
  if (trash) {
    actions.push({
      Component: DeleteDialog
    })
  }

  return (
    <TableIdProvider tableId={tableId}>
    <Table>
      {!NoTitle && <TableTitle><TitleIcon /><span>{title} List</span></TableTitle> }

      <TotalBar totals={data?.totals} />

      <TableHeader title={title} headers={use_headers} density={margin} toggleDensity={toggleDensity} tableId={tableId} filters={filters} handleSearch={handleSearch} fieldsToSearch={fieldsToSearch} createNew={createNew} disableSearch={disableSearch} exportCsv={exportCsv} />

      <FilterRibbon filters={filters}></FilterRibbon>
      {tabs.length ? <TableTabs tabs={tabs} activeTab={query.tab?.[tabField]} tabHandler={handleTabs}/> : null}

      <MainTable>
        <ScrollableTable>
          <GridTable
            role="table"
            aria-colcount={colCount}
            aria-rowcount={(data?.count || 0) + 1}
            style={{ '--cols': colTemplate }}
          >
            <Rowgroup role="rowgroup">
              <HeaderRow role="row" aria-rowindex={1}>
                <HeaderSelectCell role="columnheader" aria-label="Row details" />
                {sortedColumns.map((header) => (
                  <HeaderCell
                    key={header.label}
                    role="columnheader"
                    aria-sort={ariaSort(header)}
                    $align={header.align}
                    $gap=".4rem"
                    onClick={() => handleSort(header.id)}
                  >
                    {isMobile ? displayTruncated(header.label) : header.label}
                    <SortArrow className='sort-arrow' $visible={sorting.field === header.id}>
                      {sorting.field === header.id && sorting.dir === "desc" ? <SouthRoundedIcon fontSize='small' color={sorting.field === header.id ? "basic" : "icon"} /> : <NorthRoundedIcon fontSize='small' color={sorting.field === header.id ? "basic" : "icon"} />}
                    </SortArrow>
                  </HeaderCell>
                ))}
                {!disableActions ? (
                  <HeaderActionsCell role="columnheader" $align={isMobile ? "center" : "right"}>
                    Actions
                  </HeaderActionsCell>
                ) : null}
              </HeaderRow>
            </Rowgroup>

            <Bodygroup role="rowgroup">
              {isLoading ? <SkeletonComponent headers={use_headers} disableActions={disableActions} /> : paginateRows(data.rows).map((row, idx) => {
                const rowUrl = getRowUrl(row);

                const dataCells = sortedColumns.map((cur, cIdx) => (
                  <BodyCell
                    role="cell"
                    $margin={margin}
                    $align={cur.align}
                    $last={disableActions && cIdx === sortedColumns.length - 1}
                    key={cur.id}
                    onClick={() => clickAction && clickAction(row)}
                  >
                    {formatDisplay(cur, row, isMobile)}
                  </BodyCell>
                ));

                return (
                  <BodyRow key={row[idKey]} role="row" aria-rowindex={page * rowsPerPage + idx + 2} $view={view}>
                    <BodySelectCell role="cell" $margin={margin}>
                      <IconButton size='small' onClick={() => toggleOpenViewCard(row[idKey])}>
                        {viewCardStatus[row[idKey]] ? <KeyboardArrowDownRoundedIcon color="secondary" /> :
                        <KeyboardArrowRightRoundedIcon color="secondary" /> }
                      </IconButton>
                    </BodySelectCell>

                    {view ? (
                      <RowLink to={rowUrl} aria-label="View" draggable={false} onClick={handleRowLinkClick}>
                        {dataCells}
                      </RowLink>
                    ) : dataCells}

                    {!disableActions ? (
                      <BodyActionsCell role="cell" $margin={margin} $align={isMobile ? "center" : "right"} $gap=".6rem">
                        {!isMobile ? (
                          actions.filter(action => !action.check || action.check(row)).map(({tooltip, color, Icon, onClick, isLoading, Component}) => (Component ? <Component url={tableId} row={row} idKey={idKey} key={row[idKey]} group={groupOption} warning_msg={del_warning_msg} /> :
                            <Tooltip title={tooltip} key={tooltip}>
                              <IconBox color={muiTheme.palette[color || "secondary"].main} button size="small" bg={muiTheme.palette[color || "secondary"].light} onClick={() => onClick(row[idKey], row)}>
                                {isLoading && isLoading(row) ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <Icon color={color || "secondary"} />
                                )}
                              </IconBox>
                            </Tooltip>
                          ))
                        ) : (
                          <FloatingOptionsComponent actions={actions} url={tableId} row={row} idKey={idKey} />
                        )}
                      </BodyActionsCell>
                    ) : null}
                  </BodyRow>
                );
              })}
            </Bodygroup>
          </GridTable>
        </ScrollableTable>
      </MainTable>

      <FooterRow>
        <PageOptions>
          <TotalShowing>
            Showing: <span className='stat'>{getViewableRows(data?.rows)}</span> of {data?.count}  &nbsp; | &nbsp; {totalPages + 1} pages
          </TotalShowing>
          { isMobile ? <PageRowsMenu size="small" value={rowsPerPage} onChange={(e => handlePageRows(e))}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </PageRowsMenu> : null }
        </PageOptions>

        <PageInfo>
          <PaginationBar totalPages={totalPages} currentPage={page} onPageChange={onPageChange} />
          {!isMobile ? <PageRowsMenu size="small" value={rowsPerPage} onChange={(e => handlePageRows(e))}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </PageRowsMenu> : null }
        </PageInfo>
      </FooterRow>

    </Table>
    </TableIdProvider>
  );
};

export default TableComponent;